import { Form, Input, Select, Tooltip } from "antd";
import ProjectUserParticipantsDropdown from "components/project-participants-dropdown/project-participants-dropdown";
import SelectNotFoundContent from "components/widgets/select-notfound-content";
import SelectSearchNotFoundContent from "components/widgets/select-search-notfound-content";
import {
  CompanyParticipationType,
  ESubmittalStatus,
  UserParticipationType
} from "constants/index";

import { ProjectContext } from "context/ProjectProvider";
import { TFetureDetailUpdateAction } from "entity-app/models";
import { ProjectParticipants } from "hooks/project-participants";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import FeatureDistributionList from "components/distribution-list-submittal/feature-distribution-list";
import { updatePropertyAtPath } from "utils/utils";
import { useSubmmittialDetails } from "../hook/submittal-details-hook";

export default function SubmittalWorkflow(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  projectParticipants: ProjectParticipants;
  submittal: any;
  editableFields: any[];
  updateFeatureData: {
    localFeatureInstanceData: any;
    setLocalFeatureInstanceData: Function;
  };
  submittalViewState: any;
  isRevisionMode: boolean;
}) {
  const {
    featureInstanceData,
    actions,
    projectParticipants,
    submittal,
    editableFields,
    updateFeatureData: {
      localFeatureInstanceData,
      setLocalFeatureInstanceData
    },
    submittalViewState,
    isRevisionMode
  } = props;

  const { Option } = Select;
  const { projectId } = useParams() as any;
  const [form] = Form.useForm();
  const {
    columnHeaders: { submittalHeaderMap }
  } = useContext(ProjectContext);
  const selectedRCCompanyId = Form.useWatch("responsible_contractor", form);

  const {
    getWorkflowFieldsDisabledTooltipMsg,
    getSubmitterDisabledTooltipMsg,
    isIntegrationMode,
    cannotRespondToWorkflow,
    submittalParticipants,
    permission: { isLoggedInUserGC, isLoggedInUserSC }
  } = useSubmmittialDetails({
    featureInstanceData: localFeatureInstanceData,
    isRevisionMode,
    selectedRC: selectedRCCompanyId,
    submittalViewState
  });

  const [selectedUserCompany, setSelectedUserCompany] = useState({
    rcCompanyName: "",
    gcCompanyName: "",
    drCompanyName: ""
  });

  const responsibleContractors = () => {
    if (isLoggedInUserGC && !isIntegrationMode) {
      return projectParticipants?.responsibleContractors?.map(
        (company: any) => {
          return {
            id: company.vendor_id,
            name: company.subscription_vendor.name,
            companyInviteStatus: company.project_vendors_company_status?.status,
            ...company
          };
        }
      );
    }

    if (!submittalParticipants?.contractor) return [];

    const idStr = submittalParticipants.contractor?.vendor_source_info
      ? submittalParticipants.contractor?.vendor_source_info?.id
      : submittalParticipants.contractor?.vendor_id;

    const nameStr = submittalParticipants.contractor?.vendor_source_info
      ? submittalParticipants.contractor?.vendor_source_info?.name
      : submittalParticipants.contractor?.vendor?.name;

    const rc = [
      {
        id: idStr,
        name: nameStr,
        companyInviteStatus: ""
      }
    ];
    return rc;
  };

  useEffect(() => {
    if (!localFeatureInstanceData || !submittalParticipants) return;

    const gc = submittalParticipants.gcReviewer?.user?.project_users?.length
      ? submittalParticipants.gcReviewer?.user?.project_users[0]
          .subscription_vendor?.name
      : submittalParticipants.gcReviewer?.user?.vendor_source_info?.name ?? "";
    let dr =
      (submittalParticipants.drCompany?.vendor?.name ||
        submittalParticipants.drCompany?.vendor_unregistered ||
        submittalParticipants.drReviewer?.user?.vendor_source_info?.name) ??
      "";
    if (
      !submittalParticipants.drCompany &&
      !submittalParticipants.drReviewer?.user?.vendor_source_info &&
      projectParticipants?.designReviewers?.length
    ) {
      dr = projectParticipants?.designReviewers?.find(
        (user: any) => user?.id === submittalParticipants.drReviewer?.user?.id
      )?.company?.name;
    }

    setSelectedUserCompany({
      rcCompanyName: isIntegrationMode
        ? submittalParticipants.contractor?.vendor_source_info?.name
        : submittalParticipants.contractor?.vendor?.name,
      gcCompanyName: gc,
      drCompanyName: dr
    });

    form.setFieldsValue({
      responsible_contractor: isIntegrationMode
        ? submittalParticipants.contractor?.vendor_source_info?.id
        : submittalParticipants.contractor?.vendor_id,
      submitter: submittalParticipants.submitter?.user?.id,
      gc_reviewer: submittalParticipants.gcReviewer?.user?.id,
      design_reviewer: submittalParticipants.drReviewer?.user?.id,
      watchers: submittal?.watchers
    });
  }, [
    localFeatureInstanceData,
    form,
    submittal?.watchers,
    submittalParticipants,
    isIntegrationMode,
    projectParticipants?.designReviewers
  ]);

  const updateUserParticipants = (
    selectedUserId: string,
    users: any[],
    userType: UserParticipationType
  ) => {
    const selectedUser = users?.find(
      (user: any) => user?.id === selectedUserId
    );

    if (selectedUser) {
      const index =
        localFeatureInstanceData?.feature_user_paticipants?.findIndex(
          (object: any) => {
            return object?.user_participation_type_id === userType;
          }
        );

      let updatedObj =
        localFeatureInstanceData?.feature_user_paticipants[index];

      if (updatedObj && updatedObj?.user) {
        updatedObj.user = {
          id: selectedUser?.id,
          first_name: selectedUser?.first_name,
          last_name: selectedUser?.last_name,
          status_id: selectedUser?.status_id
        };
        localFeatureInstanceData?.feature_user_paticipants?.splice(
          index,
          1,
          updatedObj
        );
      } else {
        updatedObj = {
          user: {
            id: selectedUser?.id,
            first_name: selectedUser?.first_name,
            last_name: selectedUser?.last_name,
            status_id: selectedUser?.status_id
          },
          user_participation_type_id: userType,
          user_participation_type: {
            id: userType,
            name: UserParticipationType[userType]
          }
        };
        localFeatureInstanceData?.feature_user_paticipants.push(updatedObj);
      }

      setLocalFeatureInstanceData(
        updatePropertyAtPath(
          localFeatureInstanceData,
          "feature_user_paticipants",
          localFeatureInstanceData?.feature_user_paticipants
        )
      );
    }
  };

  const updateCompanyParticipants = (vendorId: string) => {
    const selectedCompany = responsibleContractors()?.find(
      (cmpny: any) => cmpny?.id === vendorId
    );

    if (selectedCompany) {
      const company = {
        vendor_id: vendorId,
        instance_id: localFeatureInstanceData?.id,
        company_participation_type_id:
          CompanyParticipationType.RESPONSIBLE_CONTRACTOR,
        vendor: {
          name: selectedCompany?.name
        },
        vendor_source_info: null
      };

      if (localFeatureInstanceData?.feature_company_participants?.length) {
        localFeatureInstanceData?.feature_company_participants?.splice(
          0,
          1,
          company
        );
      } else {
        localFeatureInstanceData?.feature_company_participants?.push(company);
      }

      setLocalFeatureInstanceData(
        updatePropertyAtPath(
          localFeatureInstanceData,
          "feature_company_participants",
          localFeatureInstanceData?.feature_company_participants
        )
      );
    }
  };

  const readOnlyWorkflowCard = () => {
    const submitterFullName = `${
      submittalParticipants?.submitter?.user?.first_name || ""
    } ${submittalParticipants?.submitter?.user?.last_name || ""}`;
    const gcReviewerFullName = `${
      submittalParticipants?.gcReviewer?.user?.first_name || ""
    } ${submittalParticipants?.gcReviewer?.user?.last_name || ""}`;
    const drFullName = `${
      submittalParticipants?.drReviewer?.user?.first_name || ""
    } ${submittalParticipants?.drReviewer?.user?.last_name || ""}`;
    return (
      <div data-testid="sw-readonly-root">
        <Form layout="vertical" data-testid="sw-readonly-form">
          <Form.Item label="Submitter">
            <span data-testid="sw-value-submitter">{submitterFullName}</span>
          </Form.Item>
          <Form.Item label="GC Reviewer">
            <span data-testid="sw-value-gc-reviewer">{gcReviewerFullName}</span>
          </Form.Item>
          <Form.Item label="Design Reviewer">
            <span data-testid="sw-value-design-reviewer">{drFullName}</span>
          </Form.Item>
          <FeatureDistributionList
            featureId={localFeatureInstanceData?.id}
            disabled
            data-testid="sw-distribution-list"
          />
          <Form.Item label="Responsible Contractor">
            <span data-testid="sw-value-responsible-contractor">
              {selectedUserCompany?.rcCompanyName}
            </span>
          </Form.Item>
          <Form.Item label={submittalHeaderMap?.watchers || "Watchers"}>
            <span data-testid="sw-value-watchers">{submittal?.watchers}</span>
          </Form.Item>
        </Form>
      </div>
    );
  };

  if (isLoggedInUserSC) return readOnlyWorkflowCard();

  return (
    <div
      className="grow min-h-0 h-[396px] overflow-auto"
      data-testid="sw-form-root"
    >
      <Form form={form} layout="vertical" data-testid="sw-form">
        <Tooltip
          title={getWorkflowFieldsDisabledTooltipMsg()}
          placement="rightBottom"
        >
          <Form.Item
            name="responsible_contractor"
            label={submittalHeaderMap?.responsible_contractor}
          >
            <Select
              allowClear
              onChange={(vendorId) => {
                form.setFieldValue("submitter", "");
                updateCompanyParticipants(vendorId);
                actions.updateFeatureCompanyParticipantWithEmptyUsers({
                  vendorIds: [vendorId],
                  companyParticipantTypeId:
                    CompanyParticipationType.RESPONSIBLE_CONTRACTOR,
                  userParticipationTypeId: UserParticipationType.SUBMITTER
                });
              }}
              showSearch
              filterOption
              optionFilterProp="label"
              disabled={
                isRevisionMode ||
                isIntegrationMode ||
                cannotRespondToWorkflow ||
                !editableFields.includes("responsible_contractor")
              }
              notFoundContent={
                <SelectSearchNotFoundContent
                  notFoundMsg={
                    responsibleContractors()?.length > 0
                      ? "Company not found. To add, go to "
                      : "No companies added to this project. To add, go to "
                  }
                  linkTitle="Project Companies"
                  linkPath={`/project/${projectId}/settings/general/project-companies`}
                />
              }
              data-testid="sw-select-responsible-contractor"
            >
              {responsibleContractors()?.map((company: any) => {
                return (
                  <Option
                    key={company.id}
                    value={company.id}
                    label={company.name}
                  >
                    {company.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        </Tooltip>
        <Tooltip
          title={getSubmitterDisabledTooltipMsg()}
          placement="rightBottom"
        >
          <Form.Item
            name="submitter"
            label={
              <span>
                {submittalHeaderMap?.submitter}{" "}
                {selectedUserCompany.rcCompanyName && (
                  <span className="normal-case">
                    ({selectedUserCompany.rcCompanyName})
                  </span>
                )}
              </span>
            }
          >
            <ProjectUserParticipantsDropdown
              selectedUserInfo={submittalParticipants.submitter}
              isIntegrationMode={isIntegrationMode}
              userParticipationType={UserParticipationType.SUBMITTER}
              value={submittalParticipants.submitter?.user?.id}
              selectedCompanyId={selectedRCCompanyId}
              onChange={(userId: string) => {
                updateUserParticipants(
                  userId,
                  projectParticipants.submitterUsers,
                  UserParticipationType.SUBMITTER
                );
                actions.updateFeatureUserParticipant({
                  user_id: userId,
                  user_participation_type_id: UserParticipationType.SUBMITTER
                });
              }}
              notFoundContent={
                <SelectSearchNotFoundContent
                  notFoundMsg={
                    projectParticipants.submitterUsers?.length > 0
                      ? "User is not part of above selected company. To add, go to "
                      : "There are no users added to this company. To add, go to "
                  }
                  linkTitle="Project Settings"
                  linkPath={`/project/${projectId}/settings/general/project-users`}
                />
              }
              showSearch
              optionFilterProp="label"
              filterOption
              disabled={
                isRevisionMode ||
                isIntegrationMode ||
                cannotRespondToWorkflow ||
                !selectedRCCompanyId ||
                !editableFields.includes("submitter")
              }
              data-testid="sw-select-submitter"
            />
          </Form.Item>
        </Tooltip>
        <Tooltip
          title={getWorkflowFieldsDisabledTooltipMsg()}
          placement="rightBottom"
        >
          <Form.Item
            name="gc_reviewer"
            label={
              <span>
                {submittalHeaderMap?.gc_reviewer}
                {selectedUserCompany.gcCompanyName && (
                  <span className="normal-case">
                    ({selectedUserCompany.gcCompanyName})
                  </span>
                )}
              </span>
            }
          >
            <ProjectUserParticipantsDropdown
              isIntegrationMode={isIntegrationMode}
              selectedUserInfo={submittalParticipants.gcReviewer}
              userParticipationType={UserParticipationType.GC_REVIEWER}
              onChange={(userId: string) => {
                updateUserParticipants(
                  userId,
                  projectParticipants.gcReviewers,
                  UserParticipationType.GC_REVIEWER
                );
                actions.updateFeatureUserParticipant({
                  user_id: userId,
                  user_participation_type_id: UserParticipationType.GC_REVIEWER
                });
              }}
              notFoundContent={
                <SelectNotFoundContent
                  notFoundMsg="User does not exist in the project. Go to Project Settings to add new user."
                  onAddClick={() => {
                    // setIsAddSubProjectUserDrawerOpen(true);
                  }}
                  showAddBtn={false}
                />
              }
              disabled={
                isRevisionMode ||
                isIntegrationMode ||
                cannotRespondToWorkflow ||
                !editableFields.includes("gc_reviewer")
              }
              data-testid="sw-select-gc-reviewer"
            />
          </Form.Item>
        </Tooltip>
        <Tooltip
          title={getWorkflowFieldsDisabledTooltipMsg()}
          placement="rightBottom"
        >
          <Form.Item
            name="design_reviewer"
            label={
              <span>
                {submittalHeaderMap?.design_reviewer}{" "}
                {selectedUserCompany.drCompanyName && (
                  <span className="normal-case">
                    ({selectedUserCompany.drCompanyName})
                  </span>
                )}
              </span>
            }
          >
            <ProjectUserParticipantsDropdown
              isIntegrationMode={isIntegrationMode}
              selectedUserInfo={submittalParticipants.drReviewer}
              userParticipationType={UserParticipationType.DESIGN_REVIEWER}
              onChange={(userId: string) => {
                updateUserParticipants(
                  userId,
                  projectParticipants.designReviewers,
                  UserParticipationType.DESIGN_REVIEWER
                );
                actions.updateFeatureUserParticipant({
                  user_id: userId,
                  user_participation_type_id:
                    UserParticipationType.DESIGN_REVIEWER
                });
              }}
              showSearch
              filterOption
              optionFilterProp="label"
              notFoundContent={
                <SelectSearchNotFoundContent
                  notFoundMsg={
                    projectParticipants.designReviewers?.length > 0
                      ? "Architect/Engineer is not added to project. To add, go to "
                      : "There are no Architects/Engineers added to this project. To add, go to "
                  }
                  linkTitle="Project Settings"
                  linkPath={`/project/${projectId}/settings/general/project-users`}
                />
              }
              disabled={
                isRevisionMode ||
                isIntegrationMode ||
                cannotRespondToWorkflow ||
                !editableFields.includes("design_reviewer")
              }
              data-testid="sw-select-design-reviewer"
            />
          </Form.Item>
        </Tooltip>
        <Tooltip
          title={getWorkflowFieldsDisabledTooltipMsg()}
          placement="rightBottom"
        >
          <FeatureDistributionList
            featureId={localFeatureInstanceData?.id}
            disabled={
              isRevisionMode ||
              isIntegrationMode ||
              submittalViewState?.status !== ESubmittalStatus.CREATE
            }
            data-testid="sw-distribution-list-edit"
          />
        </Tooltip>
        <Form.Item
          name="watchers"
          label={submittalHeaderMap?.watchers || "Watchers"}
        >
          <Input
            placeholder={`Enter ${submittalHeaderMap?.watchers || "Watchers"}`}
            maxLength={4000}
            disabled={
              isRevisionMode ||
              cannotRespondToWorkflow ||
              isIntegrationMode ||
              !editableFields.includes("watchers")
            }
            onBlur={async (event: any) => {
              if (submittal?.watchers === event?.target?.value?.trim()) return;
              const featureSubmittals =
                featureInstanceData?.feature_submittals?.length &&
                featureInstanceData?.feature_submittals[0];

              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "feature_submittals",
                  [
                    {
                      ...featureSubmittals,
                      watchers: event?.target?.value?.trim()
                    }
                  ]
                )
              );
              actions.updateSubmittalFields({
                watchers: event.target.value
              });
            }}
            data-testid="sw-input-watchers"
          />
        </Form.Item>
      </Form>
    </div>
  );
}
